import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { DataStore } from '../data/store';
import { useIsMounted } from '../tools';
import { useIsVisible } from '../tools/visible.hook';
import { DataOptions, DataResults, GenericWarningScope, Params } from '../types';
import { initiateState, reducer } from './reducer';

import { DEFAULT_SECTION_ERROR_MESSAGE } from '../common/constants/error';
import { useCorePageState } from '../page/provider';
import { UiDataParams, SUBSCRIPTION_ACESS_ERROR_MESSAGES, BULK_EXPORT_URL } from './constants';

import { InsightErrorType, ProviderCollectionProps } from './types';
import cache from '../cache/cache';
import { getData, getQueryKeyForData, updateDataRequestIdFromCacheData } from '../client/queryClient/data';
import { checkIfMultiConfigError } from './helpers';

type DataContext = {
  data?: DataStore;
  isDataLoading?: boolean;
  isDataLoadingFinished?: boolean;
  isEnrichmentLoading?: boolean;
  isDataOutdated?: boolean;
  error?: string;
  enrichmentError?: string;
};

const stateContext = createContext<DataContext>({});

type Props = {
  children: ReactNode;
  dataOptions?: DataOptions;
  paramsForData: Params;
  urlRoot: string;
  collection?: ProviderCollectionProps;
  isBulkExport?: boolean;
};
function DataProviderElement({
  children,
  paramsForData,
  urlRoot,
  dataOptions,
  collection,
  isBulkExport,
}: Readonly<Props>) {
  const { setGlobalWarnings, setDataLoading, dslLoading, isDslLoadedInCollection, setPageWarnings } =
    useCorePageState();
  const [state, dispatch] = useReducer(reducer, { dataOptions }, initiateState);
  const { dataParams, dataRequestId, endpoint, requestMethod } = state;
  const el = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(el);
  const isMounted = useIsMounted();
  const dataPending = useRef<{ [x: string]: boolean }>({});

  const mainDataQueryKey = useMemo(
    () => getQueryKeyForData({ dataParams, urlRoot, endpoint }),
    [urlRoot, endpoint, dataParams]
  );

  const enrichmentDataQueryKey = useMemo(
    () => getQueryKeyForData({ dataParams, urlRoot, endpoint }),
    [urlRoot, endpoint, dataParams]
  );
  const cachedMainData = updateDataRequestIdFromCacheData({ queryKey: mainDataQueryKey, dataRequestId });
  const cachedEnrichmentData = updateDataRequestIdFromCacheData({ queryKey: enrichmentDataQueryKey, dataRequestId });

  useEffect(() => {
    if (!dataOptions) {
      return;
    }
    dispatch({
      type: 'SET_DATA_OPTIONS',
      payload: dataOptions,
    });
  }, [dataOptions]);

  useEffect(() => {
    if (!paramsForData) {
      return;
    }
    dispatch({
      type: 'SET_PARAMS_FOR_DATA',
      payload: collection?.isLive ? { ...paramsForData, isLive: [String(true)] } : paramsForData,
    });
  }, [paramsForData, collection]);

  const fetchData = useCallback(async () => {
    if (dataPending.current[dataRequestId] || !state.dataOptions) return;

    const isMultiConfigError = checkIfMultiConfigError(dataParams, endpoint, collection);
    if (isMultiConfigError) {
      handleMultiConfigError();
      return;
    }

    const uiEnrichments = dataParams?.[UiDataParams.uiEnrichments] || [];

    try {
      startDataFetch();

      const data = await fetchMainData();
      handleDataSuccess(data);

      if (uiEnrichments.length) {
        dispatch({ type: 'START_DATA_ENRICH_FETCH' });
        const enrichmentData = await fetchEnrichmentData();
        dispatch({ type: 'SET_DATA_ACTION', payload: enrichmentData });

        const globalWarnings = enrichmentData?.warnings?.filter(
          warning => warning.scope === GenericWarningScope.Global
        );
        setGlobalWarnings(globalWarnings);
      }
    } catch (err) {
      handleError(err, uiEnrichments.length > 0);
    } finally {
      endDataFetch();
    }
  }, [collection, setDataLoading, setGlobalWarnings, state, urlRoot, setPageWarnings]);

  // --- Helper Functions ---
  function handleMultiConfigError() {
    dispatch({ type: 'DATA_FETCH_FAILED', payload: InsightErrorType.MULTI_CONFIG_ERROR });
    collection.onError?.({
      errorType: InsightErrorType.MULTI_CONFIG_ERROR,
      loggerMessage: 'DATA_FETCH_FAILED',
    });
  }

  function startDataFetch() {
    dispatch({ type: 'SET_IS_DATA_LOADING', payload: true });
    setDataLoading(true, dataRequestId);
    dispatch({ type: 'START_DATA_FETCH' });
    dataPending.current[dataRequestId] = true;
  }

  async function fetchMainData() {
    return await getData({
      urlRoot,
      endpoint: endpoint + (isBulkExport ? BULK_EXPORT_URL : ''),
      dataParams,
      dataRequestId,
      requestMethod,
      collection,
      cachedData: (await cachedMainData) as DataResults,
    });
  }

  async function fetchEnrichmentData() {
    return await getData({
      urlRoot,
      endpoint: endpoint + (isBulkExport ? BULK_EXPORT_URL : ''),
      dataParams,
      dataRequestId,
      requestMethod,
      collection,
      cachedData: (await cachedEnrichmentData) as DataResults,
    });
  }

  function handleDataSuccess(data: DataResults) {
    cache('data', new DataStore(data, 1));
    dispatch({ type: 'SET_DATA_ACTION', payload: data });

    const globalWarnings = data?.warnings?.filter(warning => warning.scope === GenericWarningScope.Global);
    const pageWarnings = data?.warnings?.filter(warning => warning.scope === GenericWarningScope.Local);

    typeof setGlobalWarnings === 'function' && setGlobalWarnings(globalWarnings);
    typeof setPageWarnings === 'function' && setPageWarnings(pageWarnings);
  }

  function handleError(err: any, hasEnrichments: boolean) {
    const userMessage = String(err?.message ?? DEFAULT_SECTION_ERROR_MESSAGE);
    const errorType = SUBSCRIPTION_ACESS_ERROR_MESSAGES.includes(userMessage)
      ? InsightErrorType.SUBSCRIPTION_ERROR
      : InsightErrorType.BFF_ERROR;

    collection?.onError?.({
      errorType,
      loggerMessage: 'DATA_FETCH_FAILED',
      ...(errorType === InsightErrorType.BFF_ERROR && { userMessage }),
    });

    dispatch({
      type: hasEnrichments ? 'SET_ENRICHMENT_ERROR' : 'DATA_FETCH_FAILED',
      payload: userMessage,
    });
  }

  function endDataFetch() {
    dispatch({ type: 'SET_IS_DATA_LOADING', payload: false });
    setDataLoading(false, dataRequestId);
    dataPending.current[dataRequestId] = false;
  }

  const shouldElementExist = (!collection && isVisible && isMounted()) || !!collection;
  let shouldFetchData =
    !dslLoading &&
    state.dataOptions !== undefined &&
    shouldElementExist &&
    state.isDataOutdated &&
    !dataPending.current[state.dataRequestId] &&
    !state.error &&
    !state.enrichmentError;

  // Only for collections page. Hold untill onDataChanged callback execute if needed
  if (collection) shouldFetchData = shouldFetchData && isDslLoadedInCollection;

  useEffect(() => {
    if (shouldFetchData) {
      fetchData();
    }
    // Only rerun when these change
  }, [
    shouldFetchData,
    state.dataOptions,
    state.isDataOutdated,
    state.dataRequestId,
    state.error,
    state.enrichmentError,
    isVisible,
    isMounted,
    collection,
    fetchData,
  ]);

  const value: DataContext = useMemo(() => {
    return {
      data: !state.isDataOutdated && state.data ? new DataStore(state.data, state.dataStamp) : undefined,
      isDataLoading: state.isDataLoading,
      isDataOutdated: state.isDataOutdated,
      isDataLoadingFinished: state.isDataLoadingFinished,
      isEnrichmentLoading: state.isEnrichDataLoading,
      error: state.error,
      enrichmentError: state.enrichmentError,
    };
  }, [
    state.data,
    state.dataStamp,
    state.enrichmentError,
    state.error,
    state.isDataLoading,
    state.isDataLoadingFinished,
    state.isDataOutdated,
    state.isEnrichDataLoading,
  ]);

  return (
    <div ref={el} className="data-provider w-full max-w-full">
      {state.dataOptions && <stateContext.Provider value={value}>{children}</stateContext.Provider>}
      {!state.dataOptions && <>{children}</>}
    </div>
  );
}

function useDataState() {
  const context = useContext(stateContext);
  if (!context) {
    throw new Error('useDataState must be used within a provider');
  }
  return context;
}

export { DataProviderElement, useDataState };
